import { Offer } from '../../../../services/exports/Interfaces';
import { CompleteBasketItem } from '../../../../providers/BasketProvider';
import { IOfferHandler } from '../../types';
import { useTranslation } from 'react-i18next';

const useFreeItemByProducts: IOfferHandler = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Features:Offers:OfferBadge' });
  const isCriteriaItem = (offer: Offer, item: CompleteBasketItem) => offer.details.products.includes(item.entry.id);
  const isUnlocked = (offer: Offer, items: CompleteBasketItem[]) => items.some((item) => isCriteriaItem(offer, item));
  const isEligibleItem = (offer: Offer, item: CompleteBasketItem): boolean =>
    offer.details.free_products.includes(item.entry.id);
  const isEligible = (offer: Offer, items: CompleteBasketItem[]) => items.some((item) => isEligibleItem(offer, item));
  const sumQuantity = (items: CompleteBasketItem[], predicate: (id: number) => boolean) =>
    items.reduce((acc, item) => (predicate(item.entry.id) ? acc + item.quantity : acc), 0);
  const getFreeItemsCount = (offer: Offer, items: CompleteBasketItem[]) => {
    const triggerIds: number[] = offer.details.products;
    const freeIds: number[] = offer.details.free_products;
    const overlapIds = triggerIds.filter((id) => freeIds.includes(id));
    const hasOverlap = overlapIds.length > 0;

    if (!hasOverlap) {
      return sumQuantity(items, (id) => triggerIds.includes(id));
    }

    // Overlap path: each unit can be either trigger or free, not both. Greedily free
    // pure-free items first (1 trigger per free), then overlap items (2 triggers per
    // free: itself + another), then apply the user's hard cap of 1 per offer.
    const pureTriggerQty = sumQuantity(items, (id) => triggerIds.includes(id) && !freeIds.includes(id));
    const overlapQty = sumQuantity(items, (id) => overlapIds.includes(id));
    const pureFreeQty = sumQuantity(items, (id) => freeIds.includes(id) && !triggerIds.includes(id));

    const availableTriggers = pureTriggerQty + overlapQty;
    const pureFreeFreed = Math.min(pureFreeQty, availableTriggers);
    const triggersLeftForOverlap = availableTriggers - pureFreeFreed;
    const overlapFreed = Math.min(overlapQty, Math.floor(triggersLeftForOverlap / 2));

    return Math.min(1, pureFreeFreed + overlapFreed);
  };

  const sortItemsByProfit = (offer: Offer, items: CompleteBasketItem[]) => {
    const triggerIds: number[] = offer.details.products;

    return items
      .filter((item) => isEligibleItem(offer, item))
      .sort((a, b) => {
        // Prefer pure-free items (in free_products only) over overlap items (in both
        // lists). When the user adds a trigger and picks a pure-free reward, this
        // ensures the discount lands on the picked reward, not on the trigger.
        const aIsOverlap = triggerIds.includes(a.entry.id);
        const bIsOverlap = triggerIds.includes(b.entry.id);

        if (aIsOverlap !== bIsOverlap) {
          return aIsOverlap ? 1 : -1;
        }

        return b.entry.actual_price - a.entry.actual_price;
      });
  };

  const getProfit = (offer: Offer, items: CompleteBasketItem[]): number => {
    const sortedByProfitItems = sortItemsByProfit(offer, items);

    if (!sortedByProfitItems.length) {
      return 0;
    }

    let freeItemsCount = getFreeItemsCount(offer, items);
    let totalProfit = 0;

    while (freeItemsCount !== 0 && sortedByProfitItems.length) {
      const item = sortedByProfitItems.shift();

      if (freeItemsCount >= item.quantity) {
        totalProfit += item.quantity * item.entry.actual_price;
        freeItemsCount -= item.quantity;
        continue;
      }

      totalProfit += freeItemsCount * item.entry.actual_price;
      freeItemsCount = 0;
    }

    return totalProfit;
  };

  const applyToItem = (offer: Offer, items: CompleteBasketItem[], item: CompleteBasketItem): CompleteBasketItem => {
    // Preview support: when the calling item has no original_index, it's a new item
    // being shown in MenuItemsModal before it lands in the basket. The per-item
    // discount logic below depends on the calling item being found by original_index
    // in the sorted list, which can't work for a preview. Instead, compute the
    // marginal discount this preview adds: (profit with preview) − (profit without
    // preview). That naturally yields 0 when the basket's existing items already
    // consume the free budget, and the full unit price when adding the preview
    // unlocks (or claims) a free slot.
    if (item.original_index === undefined) {
      const itemsWithoutPreview = items.filter((other) => other !== item);
      const previewDiscount = getProfit(offer, items) - getProfit(offer, itemsWithoutPreview);

      if (previewDiscount <= 0) {
        return item;
      }

      return {
        ...item,
        badges: [...item.badges, t(offer.kind)],
        actual_price: item.actual_price - previewDiscount,
      };
    }

    const sortedByProfitItems = sortItemsByProfit(offer, items);
    let freeItemsCount = getFreeItemsCount(offer, items);

    while (freeItemsCount !== 0 && sortedByProfitItems.length) {
      const sortedItem = sortedByProfitItems.shift();
      let actualPrice: number;
      let freeUnits: number;

      if (freeItemsCount >= sortedItem.quantity) {
        actualPrice = sortedItem.actual_price - sortedItem.quantity * sortedItem.entry.actual_price;
        freeUnits = sortedItem.quantity;
        freeItemsCount -= sortedItem.quantity;
      } else {
        actualPrice = sortedItem.actual_price - freeItemsCount * sortedItem.entry.actual_price;
        freeUnits = freeItemsCount;
        freeItemsCount = 0;
      }

      if (item.original_index === sortedItem.original_index) {
        return {
          ...item,
          badges: [...item.badges, t(offer.kind)],
          actual_price: actualPrice,
          offer_breakdown: {
            kind: offer.kind,
            freeQuantity: freeUnits,
            paidQuantity: item.quantity - freeUnits,
            unitPrice: item.entry?.actual_price,
          },
        };
      }
    }

    return item;
  };

  const apply =
    (offer: Offer, items: CompleteBasketItem[]) =>
    (item: CompleteBasketItem): CompleteBasketItem => {
      if (!isEligibleItem(offer, item)) {
        return item;
      }

      return applyToItem(offer, items, item);
    };

  return (offer, items) => ({
    apply: apply(offer, items),
    isEligible: isEligible(offer, items),
    isUnlocked: isUnlocked(offer, items),
    profit: getProfit(offer, items),
  });
};

export default useFreeItemByProducts;
